import { useQuery } from '@tanstack/react-query';
import { fetchDashboard } from '@/api/dashboard';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/ui/PageHeader';
import { PageSpinner } from '@/ui/Spinner';
import { QueryError } from '@/ui/QueryError';

export function DashboardPage() {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: ({ signal }) => fetchDashboard(signal),
  });

  if (isPending) return <PageSpinner label="Loading dashboard" />;
  if (isError) return <QueryError error={error} onRetry={() => void refetch()} />;

  return (
    <>
      <PageHeader title="Dashboard" />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Partners" value={data.partners} hint={`${data.active_partners} active`} />
        <Stat
          label="Organizations"
          value={data.orgs}
          hint={orgStatusSummary(data.orgs_by_status)}
        />
        <Stat
          label="Accounts needing reauth"
          value={data.accounts_needing_reauth}
          tone={data.accounts_needing_reauth > 0 ? 'destructive' : undefined}
        />
        <Stat
          label="Dead deliveries"
          value={data.dead_deliveries}
          tone={data.dead_deliveries > 0 ? 'destructive' : undefined}
        />
        <Stat
          label="Posts published"
          value={data.posts_published.last_day}
          hint={`${data.posts_published.last_week} in the last 7 days`}
        />
        <Stat
          label="Targets failed"
          value={data.targets_failed.last_day}
          hint={`${data.targets_failed.last_week} in the last 7 days`}
          tone={data.targets_failed.last_day > 0 ? 'destructive' : undefined}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Queues</CardTitle>
          <CardDescription>
            A row claimed past its lease ({data.stale_after}) counts as stale.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-3">
          {data.queues.map((queue) => (
            <div key={queue.slug} className="rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <span className="font-medium">{queue.label}</span>
                <Badge variant={queue.healthy ? 'secondary' : 'destructive'}>
                  {queue.healthy ? 'Healthy' : 'Stale work'}
                </Badge>
              </div>
              <dl className="mt-2 grid grid-cols-3 gap-2 text-sm text-muted-foreground">
                <div>
                  <dt>Due</dt>
                  <dd className="text-foreground">{queue.due}</dd>
                </div>
                <div>
                  <dt>In progress</dt>
                  <dd className="text-foreground">{queue.in_progress}</dd>
                </div>
                <div>
                  <dt>Stale</dt>
                  <dd className="text-foreground">{queue.stale}</dd>
                </div>
              </dl>
            </div>
          ))}
        </CardContent>
      </Card>
    </>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint?: string;
  tone?: 'destructive';
}) {
  return (
    <Card>
      <CardHeader>
        <CardDescription>{label}</CardDescription>
        <CardTitle className={tone === 'destructive' && value > 0 ? 'text-destructive' : undefined}>
          {value}
        </CardTitle>
      </CardHeader>
      {hint && <CardContent className="text-xs text-muted-foreground">{hint}</CardContent>}
    </Card>
  );
}

function orgStatusSummary(byStatus: Record<string, number>): string {
  return Object.entries(byStatus)
    .filter(([, count]) => count > 0)
    .map(([status, count]) => `${count} ${status.toLowerCase()}`)
    .join(', ');
}
